import { useState, useEffect, useMemo } from 'react'
import { Printer, AlertCircle, Loader2, Scale, RotateCcw, Check, CloudOff } from 'lucide-react'
import { useApp } from '../App'
import { menusAPI, airtableAPI } from '../lib/api'
import { useToastActions } from '../components/Toast'
import { useWeightsStore } from '../lib/useWeightsStore'
import PlateThis from '../components/PlateThis'
import { trIngredient, trLabel } from '../lib/translations'
import {
  buildIngredientGroups, kitchenIngredientType, SIDE_CATEGORIES, WEIGHED_TYPES, TYPE_CONFIG, OZ_PER_LB,
  fmtLbs, fmtPieces, groupedInput, groupedSaveState, ingredientGroup, setGroupedCell, sortPlatingDishes,
} from '../lib/plating'

// ══════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ══════════════════════════════════════════════════════════════════════════════

export default function Weights({ view = 'all' }) {
  const { currentMenu, currentCity, cities, isSpanish, translations } = useApp()
  const t = (label) => trLabel(isSpanish, translations, label)
  const [menu, setMenu] = useState(null)
  const [mealCounts, setMealCounts] = useState(null) // Airtable glass/plastic/custom breakdown, keyed by dish category
  const [loading, setLoading] = useState(true)
  const { error } = useToastActions()

  // Editable kitchen inputs, saved to the SERVER (shared across everyone on this
  // menu) so an employee's weigh-ins show up for the manager and anyone else.
  // Plate counts are NOT here — they come from dish.meal_count (read-only).
  //   onHand: { [ingKey]: number }       cooked qty on hand (in unit below)
  //   unit:   { [ingKey]: 'lbs' | 'ea' } input unit per ingredient
  //   piece:  { [componentId]: number }  pieces/plate for 'ea' ingredients
  //   cut:    { [ingKey]: '1' }          cut-off checklist, owned by the Prep tab
  // Storage, live-sync, per-cell save state and the retry queue all live in the
  // shared store — the Prep tab writes the same rows.
  const { inputs, setCell: patch, clearAll: resetAll, stateFor, retryFailed, failedCount, loadError, retryLoad, loading: progressLoading } = useWeightsStore(currentCity, currentMenu)

  // Load menu + per-dish counts.
  useEffect(() => {
    if (!currentMenu) {
      setMenu(null); setMealCounts(null); setLoading(false)
      return
    }
    let cancelled = false
    async function load() {
      try {
        setLoading(true)
        const menuData = await menusAPI.get(currentMenu)
        if (cancelled) return
        setMenu(menuData)
        // Per-dish glass/plastic/custom breakdown (same Airtable source as Plating).
        const importData = await airtableAPI.getLatest(currentCity, currentMenu).catch(() => null)
        if (cancelled) return
        setMealCounts(importData?.meal_counts || null)
      } catch (err) {
        if (!cancelled) error('Failed to load menu: ' + err.message)
        console.error(err)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [currentMenu, currentCity])

  // ── dishes with weighed components + their plate counts (from meal counts) ─────
  const dishes = useMemo(() => {
    const all = menu?.dishes || []
    return sortPlatingDishes(all)
      // Sides (oats/chia/broth) are pre-portioned and not weighed at plating. They're
      // base-only, so now that 'base' is weighed we must exclude them explicitly, or
      // they'd flood the weigh sheet — meals only.
      .filter(d => !SIDE_CATEGORIES.includes(d.category))
      .filter(d => (d.components || []).some(c => WEIGHED_TYPES.includes(c.component_type)))
  }, [menu])

  const totalPlates = useMemo(() => dishes.reduce((s, d) => s + (Number(d.meal_count) || 0), 0), [dishes])

  function pieceFor(componentId) {
    const value = inputs.piece[componentId]
    return value !== undefined && value !== '' ? Number(value) || 0 : 1
  }

  // ── core computation ──────────────────────────────────────────────────────────
  // need  = Σ (prescribed portion × plate count) across every dish using the ingredient
  // r     = on hand ÷ need        (= on hand ÷ total plates, generalized for unequal portions)
  // plate = prescribed portion × r
  const { dishRows, ingredients } = useMemo(() => {
    const dishRows = dishes.map(dish => {
      const plates = Number(dish.meal_count) || 0
      const comps = (dish.components || [])
        .filter(c => WEIGHED_TYPES.includes(c.component_type))
        .map(c => {
          const group = ingredientGroup(c)
          return {
          id: c.id,
          type: kitchenIngredientType(c),
          name: c.name,
          // Use the plating component name (what the Plating tab shows) — NOT the
          // generic raw_ingredient_name — so this tab mirrors Plating exactly.
          key: group.key,
          legacyKey: group.legacyKey,
          legacyKeys: group.legacyKeys,
          ingredient: group.name,
          portion_oz: Number(c.portion_oz) || 0,
          recipe_id: c.recipe_id || null,
          recipe_name: c.recipe_name || null,
        }})
        .sort((a, b) => (TYPE_CONFIG[a.type]?.order ?? 99) - (TYPE_CONFIG[b.type]?.order ?? 99) || a.name.localeCompare(b.name))
      return { dish, plates, comps }
    })

    const ing = buildIngredientGroups(dishes, inputs)
    return { dishRows, ingredients: ing }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dishes, inputs])

  const ingredientList = useMemo(() => {
    return [...ingredients.values()].sort(
      (a, b) => (TYPE_CONFIG[a.type]?.order ?? 99) - (TYPE_CONFIG[b.type]?.order ?? 99) || a.ingredient.localeCompare(b.ingredient)
    )
  }, [ingredients])

  const cityName = cities.find(c => c.id === currentCity)?.name || currentCity?.toUpperCase()
  const hasInputs = ['onHand', 'unit', 'piece', 'cut'].some(s => Object.values(inputs[s] || {}).some(v => v !== undefined && v !== ''))
  const showWeigh = view !== 'plate'
  const showPlate = view !== 'weigh'

  // ── states ────────────────────────────────────────────────────────────────────
  if (loadError) return <p role="alert">Saved progress could not load. <button onClick={retryLoad}>Retry before editing</button></p>
  if (loading || progressLoading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-havn-green-700 animate-spin" /></div>
  }
  if (!currentMenu) {
    return (
      <div className="bg-white rounded-xl border border-havn-cream-300 p-10 text-center">
        <AlertCircle className="w-8 h-8 text-havn-gray-400 mx-auto mb-3" />
        <h3 className="font-display text-lg font-semibold text-havn-charcoal mb-1">No menu selected</h3>
        <p className="text-sm text-havn-gray-500">Pick a menu from the sidebar to calculate plating weights.</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <style>{`
        @media print {
          @page { size: letter; margin: 0.5in; }
          body { background: white !important; }
          aside, header, .floating-tools, .print-hide { display: none !important; }
          main { padding: 0 !important; overflow: visible !important; }
          .weights-card { break-inside: avoid; page-break-inside: avoid; box-shadow: none !important; border: 1px solid #E5E0D6 !important; }
          input { border: 0 !important; }
        }
      `}</style>

      {/* Saves that never landed. Previously these failed silently — the number
          stayed on screen and simply wasn't there on anyone else's device. */}
      {failedCount > 0 && (
        <div className="print-hide flex items-center gap-3 px-4 py-3 rounded-lg bg-red-50 border border-red-200">
          <CloudOff className="w-4 h-4 text-red-600 flex-shrink-0" />
          <span className="text-sm text-red-700 flex-1">
            {failedCount} {failedCount === 1 ? 'entry has not saved' : 'entries have not saved'} — retrying automatically.
          </span>
          <button onClick={retryFailed} className="min-h-[36px] px-3 py-1.5 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-lg hover:bg-red-50">
            Retry now
          </button>
        </div>
      )}

      {/* Toolbar */}
      {showWeigh && <div className="print-hide flex items-start justify-between gap-3">
        <div>
          <p className="text-sm text-havn-gray-500">{t("Enter what was actually cooked. These weights flow directly from Friday prep and Chef work, then calculate each plate's amount.")}</p>
          {totalPlates === 0 && (
            <p className="text-xs text-amber-700 mt-1">
              ⚠️ No meal counts set for this menu yet — enter them on the <strong>Meal Counts</strong> tab and they'll flow in here.
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {hasInputs && (
            <button onClick={resetAll} className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-havn-cream-300 text-havn-gray-600 hover:bg-havn-cream-50 rounded-lg font-medium text-sm transition-colors">
              <RotateCcw className="w-4 h-4" /> {t('Reset all kitchen progress')}
            </button>
          )}
          <button onClick={() => window.print()} className="inline-flex items-center gap-2 px-4 py-2 bg-havn-green-700 hover:bg-havn-green-800 text-white rounded-lg font-medium text-sm transition-colors shadow-soft">
            <Printer className="w-4 h-4" /> {t('Print / Save as PDF')}
          </button>
        </div>
      </div>}

      {dishRows.length === 0 ? (
        <div className="bg-white rounded-xl border border-havn-cream-300 p-10 text-center">
          <AlertCircle className="w-8 h-8 text-havn-gray-400 mx-auto mb-3" />
          <h3 className="font-display text-lg font-semibold text-havn-charcoal mb-1">{t('Nothing to weigh yet')}</h3>
          <p className="text-sm text-havn-gray-500">{t('No dishes on this menu have protein, carb, or vegetable components.')}</p>
        </div>
      ) : (
        <>
          {/* ── SECTION 1: COOKED — ON HAND ──────────────────────────────────── */}
          {showWeigh && <section className="weights-card bg-white rounded-xl border border-havn-cream-300 overflow-hidden">
            <div className="px-5 py-4 border-b border-havn-cream-200 bg-havn-green-50 flex items-center gap-2">
              <Scale className="w-4 h-4 text-havn-green-700" />
              <h2 className="font-display text-lg font-semibold text-havn-green-900">{t("Cooked — weigh in what's on hand")}</h2>
              <span className="text-xs text-havn-gray-500">· {menu?.name} · {cityName}</span>
            </div>
            <table className="w-full">
              <thead className="bg-white">
                <tr className="text-left text-[11px] uppercase tracking-widest text-havn-gray-400 border-b border-havn-cream-200">
                  <th className="px-5 py-2.5 font-medium">{t('Type')}</th>
                  <th className="px-5 py-2.5 font-medium">{t('Ingredient')}</th>
                  <th className="px-5 py-2.5 font-medium">{t('Unit')}</th>
                  <th className="px-5 py-2.5 font-medium text-right">{t('Recipe needs')}</th>
                  <th className="px-5 py-2.5 font-medium text-right">{t('On hand')}</th>
                  <th className="px-5 py-2.5 font-medium text-right">{t('Short / Extra')}</th>
                </tr>
              </thead>
              <tbody>
                {ingredientList.map((ing) => {
                  const tc = TYPE_CONFIG[ing.type] || { label: ing.type, color: 'bg-gray-50 text-gray-700 border-gray-200' }
                  const isEa = ing.unit === 'ea'
                  const needStr = isEa ? fmtPieces(ing.need) : fmtLbs(ing.need * OZ_PER_LB)
                  const varGood = ing.variance != null && ing.variance >= 0
                  return (
                    <tr key={ing.key} className="border-b border-havn-cream-100 last:border-b-0">
                      <td className="px-5 py-3 align-middle"><span className={`inline-flex px-2 py-0.5 text-xs rounded-full border ${tc.color}`}>{t(tc.label)}</span></td>
                      <td className="px-5 py-3 align-middle text-sm font-medium text-havn-charcoal">{trIngredient(isSpanish, translations, ing.recipe_id, ing.ingredient, ing.ingredient)}</td>
                      <td className="px-5 py-3 align-middle">
                        <div className="inline-flex rounded-lg border border-havn-cream-300 overflow-hidden text-xs print-hide">
                          {['lbs', 'ea'].map(u => (
                            <button key={u} onClick={() => patch('unit', ing.key, u)}
                              className={`px-2.5 py-1 font-medium transition-colors ${ing.unit === u ? 'bg-havn-green-700 text-white' : 'bg-white text-havn-gray-500 hover:bg-havn-cream-50'}`}>
                              {u}
                            </button>
                          ))}
                        </div>
                      </td>
                      <td className="px-5 py-3 align-middle text-right text-sm text-havn-gray-500 tabular-nums">{needStr}</td>
                      <td className="px-5 py-3 align-middle text-right">
                        <div className="inline-flex items-center gap-1.5">
                          <input type="number" min="0" inputMode="decimal"
                            value={groupedInput(inputs, 'onHand', ing) ?? ''} placeholder="0"
                            onChange={(e) => setGroupedCell(patch, 'onHand', ing, e.target.value)}
                            className="w-24 px-2 py-1 text-right tabular-nums font-display text-base font-semibold text-havn-green-900 bg-white border border-havn-cream-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-havn-green-600" />
                          <span className="text-xs text-havn-gray-400 w-6 text-left">{ing.unit}</span>
                          <SaveState state={groupedSaveState(stateFor, inputs, 'onHand', ing)} t={t} />
                        </div>
                      </td>
                      <td className="px-5 py-3 align-middle text-right tabular-nums text-sm">
                        {ing.variance == null ? <span className="text-havn-gray-300">—</span>
                          : <span className={`font-semibold ${varGood ? 'text-havn-green-700' : 'text-red-600'}`}>
                              {varGood ? '+' : '−'}{isEa ? fmtPieces(Math.abs(ing.variance)) : fmtLbs(Math.abs(ing.variance) * OZ_PER_LB)}
                            </span>}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            <p className="px-5 py-2.5 text-[11px] text-havn-gray-400 border-t border-havn-cream-100">
              "Recipe needs" = prescribed portion × plate count, summed across every dish using this ingredient. Short / Extra is
              how much cooked weight you're over (+) or under (−). Set a fish like Salmon to <strong>ea</strong> to weigh by fillet
              count; leave shrimp on <strong>lbs</strong>.
            </p>
          </section>}

          {/* ── SECTION 2: PER-DISH PLATING ──────────────────────────────────── */}
          {showPlate && <PlateThis
            dishRows={dishRows}
            ingredients={ingredients}
            pieceFor={pieceFor}
            mealCounts={mealCounts}
            pieceInputs={inputs.piece}
            onPiece={(id, v) => patch('piece', id, v)}
            isSpanish={isSpanish}
            translations={translations}
            focusMode={view === 'plate'}
          />}
        </>
      )}
    </div>
  )
}

// Per-cell save state, mirroring the Prep tab. A weigh-in that never reached the
// server used to look exactly like one that did.
function SaveState({ state, t = (label) => label }) {
  const base = 'print-hide text-[11px] inline-flex items-center gap-1 w-[62px]'
  if (state === 'pending') {
    return <span className={`${base} text-havn-gray-400`}><Loader2 className="w-3.5 h-3.5 animate-spin" />{t('saving')}</span>
  }
  if (state === 'failed') {
    return <span className={`${base} text-red-600 font-medium`}><CloudOff className="w-3.5 h-3.5" />{t('not saved')}</span>
  }
  if (state === 'saved') {
    return <span className={`${base} text-havn-green-700`}><Check className="w-3.5 h-3.5" />{t('saved')}</span>
  }
  return <span className={base} aria-hidden="true" />
}
